import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { QuoteRequest, QuoteRequestDetail, CreateQuoteRequestData } from '../../types';
import api from '../../services/api';

interface QuotesState {
  quotes: QuoteRequest[];
  currentQuote: QuoteRequestDetail | null;
  loading: boolean;
  error: string | null;
}

const initialState: QuotesState = {
  quotes: [],
  currentQuote: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchQuotes = createAsyncThunk(
  'quotes/fetchQuotes',
  async () => {
    const response = await api.get('/quotes/');
    // Handle paginated response
    return response.data.results || response.data;
  }
);

export const fetchAdminQuotes = createAsyncThunk(
  'quotes/fetchAdminQuotes',
  async () => {
    const response = await api.get('/admin/quotes/');
    // Handle paginated response
    return response.data.results || response.data;
  }
);

export const fetchQuoteById = createAsyncThunk(
  'quotes/fetchQuoteById',
  async (quoteId: number) => {
    const response = await api.get(`/quotes/${quoteId}/`);
    return response.data;
  }
);

export const fetchAdminQuoteById = createAsyncThunk(
  'quotes/fetchAdminQuoteById',
  async (quoteId: number) => {
    const response = await api.get(`/admin/quotes/${quoteId}/`);
    return response.data;
  }
);

export const createQuoteRequest = createAsyncThunk(
  'quotes/createQuoteRequest',
  async (data: CreateQuoteRequestData) => {
    const response = await api.post('/quotes/request/', data);
    return response.data;
  }
);

export const acceptQuoteRequest = createAsyncThunk(
  'quotes/acceptQuoteRequest',
  async (quoteId: number) => {
    const response = await api.post(`/quotes/${quoteId}/accept/`, {});
    return response.data;
  }
);

export interface RespondToQuoteData {
  admin_response: string;
  quoted_price: number;
  quote_valid_until: string;
  status: 'replied' | 'rejected';
}

export const respondToQuote = createAsyncThunk(
  'quotes/respondToQuote',
  async ({ quoteId, data }: { quoteId: number; data: RespondToQuoteData }) => {
    const response = await api.post(`/admin/quotes/${quoteId}/respond/`, data);
    return response.data;
  }
);

const quotesSlice = createSlice({
  name: 'quotes',
  initialState,
  reducers: {
    clearCurrentQuote: (state) => {
      state.currentQuote = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch quotes
      .addCase(fetchQuotes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuotes.fulfilled, (state, action) => {
        state.loading = false;
        state.quotes = action.payload;
      })
      .addCase(fetchQuotes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Hiba történt az árajánlatok betöltése során';
      })

      // Fetch admin quotes
      .addCase(fetchAdminQuotes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminQuotes.fulfilled, (state, action) => {
        state.loading = false;
        state.quotes = action.payload;
      })
      .addCase(fetchAdminQuotes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Hiba történt az árajánlatok betöltése során';
      })

      // Fetch quote by ID
      .addCase(fetchQuoteById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuoteById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentQuote = action.payload;
      })
      .addCase(fetchQuoteById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Hiba történt az árajánlat betöltése során';
      })

      // Fetch admin quote by ID
      .addCase(fetchAdminQuoteById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminQuoteById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentQuote = action.payload;
      })
      .addCase(fetchAdminQuoteById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Hiba történt az árajánlat betöltése során';
      })

      // Create quote request
      .addCase(createQuoteRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createQuoteRequest.fulfilled, (state, action) => {
        state.loading = false;
        // Add the new quote to the list if it exists
        if (action.payload.quote_request) {
          state.quotes.unshift(action.payload.quote_request);
        }
      })
      .addCase(createQuoteRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Hiba történt az árajánlat kérés elküldése során';
      })

      // Accept quote request
      .addCase(acceptQuoteRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(acceptQuoteRequest.fulfilled, (state, action) => {
        state.loading = false;
        // Update the quote status in the list
        const quoteIndex = state.quotes.findIndex(q => q.id === state.currentQuote?.id);
        if (quoteIndex !== -1) {
          state.quotes[quoteIndex].status = 'converted';
        }
        if (state.currentQuote) {
          state.currentQuote.status = 'converted';
        }
      })
      .addCase(acceptQuoteRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Hiba történt az árajánlat elfogadása során';
      })

      // Respond to quote request (admin)
      .addCase(respondToQuote.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(respondToQuote.fulfilled, (state, action) => {
        state.loading = false;
        // Update the quote in the list
        const quoteIndex = state.quotes.findIndex(q => q.id === action.payload.id);
        if (quoteIndex !== -1) {
          state.quotes[quoteIndex] = action.payload;
        }
        // Update current quote if it's the one being responded to
        if (state.currentQuote && state.currentQuote.id === action.payload.id) {
          state.currentQuote = action.payload;
        }
      })
      .addCase(respondToQuote.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Hiba történt az árajánlat válasz elküldése során';
      });
  },
});

export const { clearCurrentQuote, clearError } = quotesSlice.actions;

export default quotesSlice.reducer;